import Decimal from "decimal.js-light";

import {
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_FEET_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_FEET_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_LABEL,
} from "@/utils/constants";
import {
  getBasicQuantity,
  getCustomFormulaQuantity,
  getLinealFeetQuantityInFeet,
  getLinealMetresQuantityInMetres,
  getSquareFeetQuantityInSquareFeet,
  getSquareMetresQuantityInSquareMetres,
} from "@/utils/measurement/measurement-for-price-computation";

import calculateTotalLabourPrice from "./sales-order-price/calculate-total-labour-price";

Decimal.config({ rounding: Decimal.ROUND_HALF_UP });

/**
 * Applies item-level discount and returns the discounted line total rounded
 * to 2 decimal places (cents) — this is the amount shown on the invoice line.
 * For percentage discounts: total × (1 - discount/100)
 * For dollar discounts: total - discount
 *
 * The 2dp round lives here (not at each call site) because every consumer of
 * this value is a final line total: it is either summed as-is, or used as the
 * GST base. For GST the 2dp round is mandatory before applying the tax rate
 * (CUS-505) — keeping it in the helper makes it impossible for a new pricing
 * branch to forget it. No caller feeds the raw value into a longer chain.
 */
const applyItemDiscount = ({
  productTotal = Decimal(0),
  discount = Decimal(0),
  discountIsPercentage = true,
} = {}) => {
  if (discountIsPercentage === false) {
    return productTotal.minus(discount).toDecimalPlaces(2);
  }
  const discountRate = discount.dividedBy(100).toDecimalPlaces(2);
  return productTotal.times(Decimal(1).minus(discountRate)).toDecimalPlaces(2);
};

export const findPricePerMetreDrawing = (options) => {
  const { numberOfBends, totalGirth, dataServer, tapers } = options;

  let value = Decimal(0);
  const productsGirth = dataServer.filter(
    (item) =>
      totalGirth >= parseFloat(item.get("girthMin")) &&
      totalGirth <= parseFloat(item.get("girthMax"))
  );
  let maxBend = -1;
  let maxProductBend;
  const product = productsGirth.find((item) => {
    const bend = parseFloat(item.get("bends"));

    if (maxBend < bend) {
      maxBend = bend;
      maxProductBend = item;
    }

    if (numberOfBends === bend) {
      return item;
    }

    return null;
  });

  if (product) {
    value = Decimal(product.get("price"));
  } else if (maxProductBend && maxProductBend.get("extraBendFee") > 0) {
    const extraBends = Decimal(numberOfBends).minus(maxProductBend.get("bends"));
    value = Decimal(maxProductBend.get("price")).plus(
      extraBends.times(maxProductBend.get("extraBendFee"))
    );
  } else if (maxProductBend) {
    value = Decimal(maxProductBend.get("price"));
  }

  if (tapers && (product || maxProductBend)) {
    const maxProduct = product || maxProductBend;
    let tapersValue = Decimal(maxProduct.get("taperedFee"));

    if (maxProduct.get("addTapersCharge")) {
      tapersValue = tapersValue.times(tapers);
    }

    value = value.plus(tapersValue);
  }

  return value.toDecimalPlaces(2).toFixed(2);
};

export const calcPriceAdditionalProducts = ({
  items,
  minLengthProducts = 0,
  minLengthLinealFeet = 0,
  // sumDiscountPercent = "0",
  taxRate = 0.1,
}) => {
  // const sumDiscount = Decimal(sumDiscountPercent);

  const sumAdditionalProducts = items
    .filter((p) => p.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL)
    .reduce((sum, item) => {
      if (item.isDeleted) {
        return Decimal(sum);
      }

      if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL && !item.customPricing) {
        // calculate based on kit items
        if (!Array.isArray(item.productKitItems)) {
          return Decimal(sum);
        }
        const kitItemTotals = item.productKitItems.reduce((kitSum, kitItem) => {
          // if (kitItem.customValue) {
          //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
          //   const discountedCustomValue = Decimal(kitItem.customValue)
          //     .times(Decimal(1).minus(discountRate))
          //     .toDecimalPlaces(2);
          //   return Decimal(kitSum).plus(discountedCustomValue);
          // }

          if (!kitItem.value) {
            return Decimal(kitSum);
          }

          const ownDiscount = Decimal(kitItem.discount ? kitItem.discount : 0);
          const kitItemDiscountIsPercentage = kitItem.discountIsPercentage ?? true;

          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { forPrice } = getLinealMetresQuantityInMetres({
              items: kitItem.lengthItems,
              minLengthInMm: minLengthProducts,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(forPrice),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });

            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
              items: kitItem.lengthItems,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(sqmTotal),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });

            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
            const formulaQuantity = getCustomFormulaQuantity({
              quantity: kitItem.quantity,
              formulaOutput: kitItem.customFormulaOutput,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(formulaQuantity),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });

            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { forPrice } = getLinealFeetQuantityInFeet({
              items: kitItem.lengthItems,
              minLengthInMm: minLengthLinealFeet,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(forPrice),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
              items: kitItem.lengthItems,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(sqFtTotal),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          const quantity = getBasicQuantity({ quantity: kitItem.quantity });
          const discountedItemTotal = applyItemDiscount({
            productTotal: Decimal(kitItem.value).times(quantity),
            discount: ownDiscount,
            discountIsPercentage: kitItemDiscountIsPercentage,
          });
          return Decimal(kitSum).plus(discountedItemTotal);
        }, 0);

        if (!Array.isArray(item.subKits)) {
          return Decimal(sum);
        }

        const subKitTotals = item.subKits.reduce((subKitSum, subKit) => {
          const subKitItemTotals = subKit.kitProducts.reduce((kitSum, kitItem) => {
            // if (kitItem.customValue) {
            //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
            //   const discountedCustomValue = Decimal(kitItem.customValue)
            //     .times(Decimal(1).minus(discountRate))
            //     .toDecimalPlaces(2);
            //   return Decimal(kitSum).plus(discountedCustomValue);
            // }

            if (!kitItem.value) {
              return Decimal(kitSum);
            }

            const ownDiscount = Decimal(kitItem.discount ? kitItem.discount : 0);
            const kitItemDiscountIsPercentage = kitItem.discountIsPercentage ?? true;

            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { forPrice } = getLinealMetresQuantityInMetres({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthProducts,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(forPrice),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                items: kitItem.lengthItems,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(sqmTotal),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              const formulaQuantity = getCustomFormulaQuantity({
                quantity: kitItem.quantity,
                formulaOutput: kitItem.customFormulaOutput,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(formulaQuantity),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { forPrice } = getLinealFeetQuantityInFeet({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthLinealFeet,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(forPrice),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                items: kitItem.lengthItems,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(sqFtTotal),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            const quantity = getBasicQuantity({ quantity: kitItem.quantity });
            const discountedItemTotal = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(quantity),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(discountedItemTotal);
          }, 0);
          return Decimal(subKitSum).plus(subKitItemTotals);
        }, 0);

        return Decimal(sum).plus(kitItemTotals).plus(subKitTotals);
      }

      // if (item.customValue) {
      //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
      //   const discountedCustomValue = Decimal(item.customValue)
      //     .times(Decimal(1).minus(discountRate))
      //     .toDecimalPlaces(2);
      //   return Decimal(sum).plus(discountedCustomValue);
      // }

      if (!item.value) {
        return Decimal(sum);
      }

      const ownDiscount = Decimal(item.discount ? item.discount : 0);
      const itemDiscountIsPercentage = item.discountIsPercentage ?? true;

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { forPrice } = getLinealMetresQuantityInMetres({
          items: item.subitems,
          minLengthInMm: minLengthProducts,
        });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(forPrice),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(productTotalWithDiscount);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({ items: item.subitems });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(sqmTotal),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(productTotalWithDiscount);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const formulaQuantity = getCustomFormulaQuantity({
          quantity: item.quantity,
          formulaOutput: item.customFormulaOutput,
        });
        const discountedItemTotal = applyItemDiscount({
          productTotal: Decimal(item.value).times(formulaQuantity),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(discountedItemTotal);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { forPrice } = getLinealFeetQuantityInFeet({
          items: item.subitems,
          minLengthInMm: minLengthLinealFeet,
        });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(forPrice),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });
        return Decimal(sum).plus(productTotalWithDiscount);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({ items: item.subitems });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(sqFtTotal),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });
        return Decimal(sum).plus(productTotalWithDiscount);
      }

      const quantity = getBasicQuantity({ quantity: item.quantity });
      const discountedItemTotal = applyItemDiscount({
        productTotal: Decimal(item.value).times(quantity),
        discount: ownDiscount,
        discountIsPercentage: itemDiscountIsPercentage,
      });
      return Decimal(sum).plus(discountedItemTotal);
    }, 0);

  const gstAdditionalProducts = items
    .filter((p) => p.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL)
    .reduce((sum, item) => {
      if (item.isDeleted) {
        return Decimal(sum);
      }

      if (!item.value) {
        return Decimal(sum);
      }

      if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL && !item.customPricing) {
        if (!Array.isArray(item.productKitItems)) {
          return Decimal(sum);
        }
        // calculate based on kit items
        const kitGst = item.productKitItems
          .filter((p) => !p.isTaxFree)
          .reduce((kitSum, kitItem) => {
            // if (kitItem.customValue) {
            //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
            //   const GST = Decimal(kitItem.customValue)
            //     .times(Decimal(1).minus(discountRate))
            //     .times(Decimal(taxRate))
            //     .toDecimalPlaces(2);
            //   return Decimal(kitSum).plus(GST);
            // }

            if (!kitItem.value) {
              return Decimal(kitSum);
            }

            const ownDiscount = Decimal(kitItem.discount ? kitItem.discount : 0);
            const kitItemDiscountIsPercentage = kitItem.discountIsPercentage ?? true;

            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { forPrice } = getLinealMetresQuantityInMetres({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthProducts,
              });
              const GST = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(forPrice),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              })
                .times(Decimal(taxRate))
                .toDecimalPlaces(2);
              return Decimal(kitSum).plus(GST);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                items: kitItem.lengthItems,
              });
              const GST = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(sqmTotal),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              })
                .times(Decimal(taxRate))
                .toDecimalPlaces(2);
              return Decimal(kitSum).plus(GST);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              const formulaQuantity = getCustomFormulaQuantity({
                quantity: kitItem.quantity,
                formulaOutput: kitItem.customFormulaOutput,
              });
              const GST = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(formulaQuantity),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              })
                .times(Decimal(taxRate))
                .toDecimalPlaces(2);
              return Decimal(kitSum).plus(GST);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { forPrice } = getLinealFeetQuantityInFeet({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthLinealFeet,
              });
              const GST = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(forPrice),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              })
                .times(Decimal(taxRate))
                .toDecimalPlaces(2);
              return Decimal(kitSum).plus(GST);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                items: kitItem.lengthItems,
              });
              const GST = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(sqFtTotal),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              })
                .times(Decimal(taxRate))
                .toDecimalPlaces(2);
              return Decimal(kitSum).plus(GST);
            }

            const quantity = getBasicQuantity({ quantity: kitItem.quantity });
            const GST = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(quantity),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            })
              .times(Decimal(taxRate))
              .toDecimalPlaces(2);
            return Decimal(kitSum).plus(GST);
          }, 0);

        if (!Array.isArray(item.subKits)) {
          return Decimal(sum);
        }

        const subKitsGst = item.subKits.reduce((subKitSum, subKit) => {
          const subKitGst = subKit.kitProducts
            .filter((p) => !p.isTaxFree)
            .reduce((kitSum, kitItem) => {
              // if (kitItem.customValue) {
              //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
              //   const GST = Decimal(kitItem.customValue)
              //     .times(Decimal(1).minus(discountRate))
              //     .times(Decimal(taxRate))
              //     .toDecimalPlaces(2);
              //   return Decimal(kitSum).plus(GST);
              // }

              if (!kitItem.value) {
                return Decimal(kitSum);
              }

              const ownDiscount = Decimal(kitItem.discount ? kitItem.discount : 0);
              const kitItemDiscountIsPercentage = kitItem.discountIsPercentage ?? true;

              if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
                if (!kitItem.lengthItems?.length) {
                  return Decimal(kitSum);
                }
                const { forPrice } = getLinealMetresQuantityInMetres({
                  items: kitItem.lengthItems,
                  minLengthInMm: minLengthProducts,
                });
                const GST = applyItemDiscount({
                  productTotal: Decimal(kitItem.value).times(forPrice),
                  discount: ownDiscount,
                  discountIsPercentage: kitItemDiscountIsPercentage,
                })
                  .times(Decimal(taxRate))
                  .toDecimalPlaces(2);
                return Decimal(kitSum).plus(GST);
              }

              if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
                if (!kitItem.lengthItems?.length) {
                  return Decimal(kitSum);
                }
                const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                  items: kitItem.lengthItems,
                });
                const GST = applyItemDiscount({
                  productTotal: Decimal(kitItem.value).times(sqmTotal),
                  discount: ownDiscount,
                  discountIsPercentage: kitItemDiscountIsPercentage,
                })
                  .times(Decimal(taxRate))
                  .toDecimalPlaces(2);
                return Decimal(kitSum).plus(GST);
              }

              if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
                const formulaQuantity = getCustomFormulaQuantity({
                  quantity: kitItem.quantity,
                  formulaOutput: kitItem.customFormulaOutput,
                });
                const GST = applyItemDiscount({
                  productTotal: Decimal(kitItem.value).times(formulaQuantity),
                  discount: ownDiscount,
                  discountIsPercentage: kitItemDiscountIsPercentage,
                })
                  .times(Decimal(taxRate))
                  .toDecimalPlaces(2);
                return Decimal(kitSum).plus(GST);
              }

              if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
                if (!kitItem.lengthItems?.length) {
                  return Decimal(kitSum);
                }
                const { forPrice } = getLinealFeetQuantityInFeet({
                  items: kitItem.lengthItems,
                  minLengthInMm: minLengthLinealFeet,
                });
                const GST = applyItemDiscount({
                  productTotal: Decimal(kitItem.value).times(forPrice),
                  discount: ownDiscount,
                  discountIsPercentage: kitItemDiscountIsPercentage,
                })
                  .times(Decimal(taxRate))
                  .toDecimalPlaces(2);
                return Decimal(kitSum).plus(GST);
              }

              if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
                if (!kitItem.lengthItems?.length) {
                  return Decimal(kitSum);
                }
                const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                  items: kitItem.lengthItems,
                });
                const GST = applyItemDiscount({
                  productTotal: Decimal(kitItem.value).times(sqFtTotal),
                  discount: ownDiscount,
                  discountIsPercentage: kitItemDiscountIsPercentage,
                })
                  .times(Decimal(taxRate))
                  .toDecimalPlaces(2);
                return Decimal(kitSum).plus(GST);
              }

              const quantity = getBasicQuantity({ quantity: kitItem.quantity });
              const GST = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(quantity),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              })
                .times(Decimal(taxRate))
                .toDecimalPlaces(2);
              return Decimal(kitSum).plus(GST);
            }, 0);
          return Decimal(subKitSum).plus(subKitGst);
        }, 0);

        return Decimal(sum).plus(kitGst).plus(subKitsGst);
      }

      if (item.isTaxFree) {
        return Decimal(sum);
      }

      // if (item.customValue) {
      //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
      //   const GST = Decimal(item.customValue)
      //     .times(Decimal(1).minus(discountRate))
      //     .times(Decimal(taxRate))
      //     .toDecimalPlaces(2);
      //   return Decimal(sum).plus(GST);
      // }

      const ownDiscount = Decimal(item.discount ? item.discount : 0);
      const itemDiscountIsPercentage = item.discountIsPercentage ?? true;

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { forPrice } = getLinealMetresQuantityInMetres({
          items: item.subitems,
          minLengthInMm: minLengthProducts,
        });
        const GST = applyItemDiscount({
          productTotal: Decimal(item.value).times(forPrice),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        })
          .times(Decimal(taxRate))
          .toDecimalPlaces(2);
        return Decimal(sum).plus(GST);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({ items: item.subitems });
        const GST = applyItemDiscount({
          productTotal: Decimal(item.value).times(sqmTotal),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        })
          .times(Decimal(taxRate))
          .toDecimalPlaces(2);
        return Decimal(sum).plus(GST);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { forPrice } = getLinealFeetQuantityInFeet({
          items: item.subitems,
          minLengthInMm: minLengthLinealFeet,
        });
        const GST = applyItemDiscount({
          productTotal: Decimal(item.value).times(forPrice),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        })
          .times(Decimal(taxRate))
          .toDecimalPlaces(2);
        return Decimal(sum).plus(GST);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({ items: item.subitems });
        const GST = applyItemDiscount({
          productTotal: Decimal(item.value).times(sqFtTotal),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        })
          .times(Decimal(taxRate))
          .toDecimalPlaces(2);
        return Decimal(sum).plus(GST);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const formulaQuantity = getCustomFormulaQuantity({
          quantity: item.quantity,
          formulaOutput: item.customFormulaOutput,
        });
        const GST = applyItemDiscount({
          productTotal: Decimal(item.value).times(formulaQuantity),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        })
          .times(Decimal(taxRate))
          .toDecimalPlaces(2);
        return Decimal(sum).plus(GST);
      }

      const quantity = getBasicQuantity({ quantity: item.quantity });
      const GST = applyItemDiscount({
        productTotal: Decimal(item.value).times(quantity),
        discount: ownDiscount,
        discountIsPercentage: itemDiscountIsPercentage,
      })
        .times(Decimal(taxRate))
        .toDecimalPlaces(2);
      return Decimal(sum).plus(GST);
    }, 0);

  // Actual Cost
  const costAdditionalProducts = items
    .filter((p) => p.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL)
    .reduce((sum, item) => {
      if (item.isDeleted) {
        return Decimal(sum);
      }

      // Standard and custom kits calc
      if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
        if (!Array.isArray(item.productKitItems)) {
          return Decimal(sum);
        }
        // calculate based on kit items
        const kitCost = item.productKitItems.reduce((kitSum, kitItem) => {
          const cost = kitItem.cost ? Decimal(kitItem.cost) : null;

          if (!cost || cost <= 0) {
            return Decimal(kitSum);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
            const totalCost = cost.times(getBasicQuantity({ quantity: kitItem.actualQuantity }));
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
            const { forPrice } = getLinealMetresQuantityInMetres({
              items: kitItem.actualLengthItems,
              minLengthInMm: minLengthProducts,
            });
            const totalCost = cost.times(forPrice);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
            const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
              items: kitItem.actualLengthItems,
            });
            const totalCost = cost.times(sqmTotal);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
            const formulaQuantity = getCustomFormulaQuantity({
              quantity: kitItem.actualQuantity,
              formulaOutput: kitItem.actualCustomFormulaOutput,
            });
            const totalCost = cost.times(formulaQuantity);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
            const { forPrice } = getLinealFeetQuantityInFeet({
              items: kitItem.actualLengthItems,
              minLengthInMm: minLengthLinealFeet,
            });
            const totalCost = cost.times(forPrice);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
            const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
              items: kitItem.actualLengthItems,
            });
            const totalCost = cost.times(sqFtTotal);
            return Decimal(kitSum).plus(totalCost);
          }

          return Decimal(kitSum);
        }, 0);

        if (!Array.isArray(item.subKits)) {
          return Decimal(sum);
        }

        const subKitsCost = item.subKits.reduce((subKitSum, subKit) => {
          const subKitCost = subKit.kitProducts.reduce((kitSum, kitItem) => {
            const cost = kitItem.cost ? Decimal(kitItem.cost) : null;

            if (!cost || cost <= 0) {
              return Decimal(kitSum);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
              const totalCost = cost.times(getBasicQuantity({ quantity: kitItem.actualQuantity }));
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
              const { forPrice } = getLinealMetresQuantityInMetres({
                items: kitItem.actualLengthItems,
                minLengthInMm: minLengthProducts,
              });
              const totalCost = cost.times(forPrice);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
              const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                items: kitItem.actualLengthItems,
              });
              const totalCost = cost.times(sqmTotal);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              const formulaQuantity = getCustomFormulaQuantity({
                quantity: kitItem.actualQuantity,
                formulaOutput: kitItem.actualCustomFormulaOutput,
              });
              const totalCost = cost.times(formulaQuantity);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
              const { forPrice } = getLinealFeetQuantityInFeet({
                items: kitItem.actualLengthItems,
                minLengthInMm: minLengthLinealFeet,
              });
              const totalCost = cost.times(forPrice);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
              const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                items: kitItem.actualLengthItems,
              });
              const totalCost = cost.times(sqFtTotal);
              return Decimal(kitSum).plus(totalCost);
            }

            return Decimal(kitSum);
          }, 0);
          return Decimal(subKitSum).plus(subKitCost);
        }, 0);

        return Decimal(sum).plus(kitCost).plus(subKitsCost);
      }

      const cost = item.cost ? Decimal(item.cost) : null;

      if (!cost || cost <= 0) {
        return Decimal(sum);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
        const totalCost = cost.times(getBasicQuantity({ quantity: item.actualQuantity }));
        return Decimal(sum).plus(totalCost);
      }
      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
        const { forPrice } = getLinealMetresQuantityInMetres({
          items: item.actualLengthItems,
          minLengthInMm: minLengthProducts,
        });
        const totalCost = cost.times(forPrice);
        return Decimal(sum).plus(totalCost);
      }
      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
        const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
          items: item.actualLengthItems,
        });
        const totalCost = cost.times(sqmTotal);
        return Decimal(sum).plus(totalCost);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
        const { forPrice } = getLinealFeetQuantityInFeet({
          items: item.actualLengthItems,
          minLengthInMm: minLengthLinealFeet,
        });
        const totalCost = cost.times(forPrice);
        return Decimal(sum).plus(totalCost);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
        const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
          items: item.actualLengthItems,
        });
        const totalCost = cost.times(sqFtTotal);
        return Decimal(sum).plus(totalCost);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const formulaQuantity = getCustomFormulaQuantity({
          quantity: item.actualQuantity,
          formulaOutput: item.actualCustomFormulaOutput,
        });
        const totalCost = cost.times(formulaQuantity);
        return Decimal(sum).plus(totalCost);
      }

      return Decimal(sum);
    }, 0);

  const estimatedCostAdditionalProducts = items
    .filter((p) => p.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL)
    .reduce((sum, item) => {
      if (item.isDeleted) {
        return Decimal(sum);
      }

      // Standard kits calc
      if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
        if (!Array.isArray(item.productKitItems)) {
          return Decimal(sum);
        }
        // calculate based on kit items
        const kitCost = item.productKitItems.reduce((kitSum, kitItem) => {
          const cost = kitItem.cost ? Decimal(kitItem.cost) : null;

          if (!cost || cost <= 0) {
            return Decimal(kitSum);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
            const totalCost = cost.times(getBasicQuantity({ quantity: kitItem.quantity }));
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
            const { forPrice } = getLinealMetresQuantityInMetres({
              items: kitItem.lengthItems,
              minLengthInMm: minLengthProducts,
            });
            const totalCost = cost.times(forPrice);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
            const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
              items: kitItem.lengthItems,
            });
            const totalCost = cost.times(sqmTotal);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
            const formulaQuantity = getCustomFormulaQuantity({
              quantity: kitItem.quantity,
              formulaOutput: kitItem.customFormulaOutput,
            });
            const totalCost = cost.times(formulaQuantity);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
            const { forPrice } = getLinealFeetQuantityInFeet({
              items: kitItem.lengthItems,
              minLengthInMm: minLengthLinealFeet,
            });
            const totalCost = cost.times(forPrice);
            return Decimal(kitSum).plus(totalCost);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
            const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
              items: kitItem.lengthItems,
            });
            const totalCost = cost.times(sqFtTotal);
            return Decimal(kitSum).plus(totalCost);
          }

          return Decimal(kitSum);
        }, 0);

        if (!Array.isArray(item.subKits)) {
          return Decimal(sum);
        }

        const subKitsCost = item.subKits.reduce((subKitSum, subKit) => {
          const subKitCost = subKit.kitProducts.reduce((kitSum, kitItem) => {
            const cost = kitItem.cost ? Decimal(kitItem.cost) : null;

            if (!cost || cost <= 0) {
              return Decimal(kitSum);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
              const totalCost = cost.times(getBasicQuantity({ quantity: kitItem.quantity }));
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
              const { forPrice } = getLinealMetresQuantityInMetres({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthProducts,
              });
              const totalCost = cost.times(forPrice);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
              const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                items: kitItem.lengthItems,
              });
              const totalCost = cost.times(sqmTotal);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              const formulaQuantity = getCustomFormulaQuantity({
                quantity: kitItem.quantity,
                formulaOutput: kitItem.customFormulaOutput,
              });
              const totalCost = cost.times(formulaQuantity);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
              const { forPrice } = getLinealFeetQuantityInFeet({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthLinealFeet,
              });
              const totalCost = cost.times(forPrice);
              return Decimal(kitSum).plus(totalCost);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
              const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                items: kitItem.lengthItems,
              });
              const totalCost = cost.times(sqFtTotal);
              return Decimal(kitSum).plus(totalCost);
            }

            return Decimal(kitSum);
          }, 0);
          return Decimal(subKitSum).plus(subKitCost);
        }, 0);

        return Decimal(sum).plus(kitCost).plus(subKitsCost);
      }

      const cost = item.cost ? Decimal(item.cost) : null;

      if (!cost || cost <= 0) {
        return Decimal(sum);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
        const totalCost = cost.times(getBasicQuantity({ quantity: item.quantity }));
        return Decimal(sum).plus(totalCost);
      }
      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
        const { forPrice } = getLinealMetresQuantityInMetres({
          items: item.subitems,
          minLengthInMm: minLengthProducts,
        });
        const totalCost = cost.times(forPrice);
        return Decimal(sum).plus(totalCost);
      }
      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
        const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({ items: item.subitems });
        const totalCost = cost.times(sqmTotal);
        return Decimal(sum).plus(totalCost);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
        const { forPrice } = getLinealFeetQuantityInFeet({
          items: item.subitems,
          minLengthInMm: minLengthLinealFeet,
        });
        const totalCost = cost.times(forPrice);
        return Decimal(sum).plus(totalCost);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const formulaQuantity = getCustomFormulaQuantity({
          quantity: item.quantity,
          formulaOutput: item.customFormulaOutput,
        });
        const totalCost = cost.times(formulaQuantity);
        return Decimal(sum).plus(totalCost);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
        const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({ items: item.subitems });
        const totalCost = cost.times(sqFtTotal);
        return Decimal(sum).plus(totalCost);
      }

      return Decimal(sum);
    }, 0);

  const sumMarkupAdditionalProducts = items
    .filter((p) => p.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL)
    .reduce((sum, item) => {
      if (item.isDeleted) {
        return Decimal(sum);
      }

      if (!item.value) {
        return Decimal(sum);
      }

      if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL && !item.customPricing) {
        if (!Array.isArray(item.productKitItems)) {
          return Decimal(sum);
        }
        // calculate based on kit items
        const kitMarkup = item.productKitItems.reduce((kitSum, kitItem) => {
          if (!kitItem.cost || Decimal(kitItem.cost) <= 0) {
            return Decimal(kitSum);
          }

          // if (kitItem.customValue) {
          //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
          //   const discountedCustomValue = Decimal(kitItem.customValue)
          //     .times(Decimal(1).minus(discountRate))
          //     .toDecimalPlaces(2);
          //   return Decimal(kitSum).plus(discountedCustomValue);
          // }

          if (!kitItem.value) {
            return Decimal(kitSum);
          }

          const ownDiscount = Decimal(kitItem.discount ? kitItem.discount : 0);
          const kitItemDiscountIsPercentage = kitItem.discountIsPercentage ?? true;

          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { forPrice } = getLinealMetresQuantityInMetres({
              items: kitItem.lengthItems,
              minLengthInMm: minLengthProducts,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(forPrice),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });

            return Decimal(kitSum).plus(productTotalWithDiscount);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
              items: kitItem.lengthItems,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(sqmTotal),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });

            return Decimal(kitSum).plus(productTotalWithDiscount);
          }
          if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
            const formulaQuantity = getCustomFormulaQuantity({
              quantity: kitItem.quantity,
              formulaOutput: kitItem.customFormulaOutput,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(formulaQuantity),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { forPrice } = getLinealFeetQuantityInFeet({
              items: kitItem.lengthItems,
              minLengthInMm: minLengthLinealFeet,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(forPrice),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
            if (!kitItem.lengthItems?.length) {
              return Decimal(kitSum);
            }
            const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
              items: kitItem.lengthItems,
            });
            const productTotalWithDiscount = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(sqFtTotal),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(productTotalWithDiscount);
          }

          const quantity = getBasicQuantity({ quantity: kitItem.quantity });
          const discountedItemTotal = applyItemDiscount({
            productTotal: Decimal(kitItem.value).times(quantity),
            discount: ownDiscount,
            discountIsPercentage: kitItemDiscountIsPercentage,
          });
          return Decimal(kitSum).plus(discountedItemTotal);
        }, 0);

        if (!Array.isArray(item.subKits)) {
          return Decimal(sum);
        }

        const subKitsMarkup = item.subKits.reduce((subKitSum, subKit) => {
          const subKitMarkup = subKit.kitProducts.reduce((kitSum, kitItem) => {
            if (!kitItem.cost || Decimal(kitItem.cost) <= 0) {
              return Decimal(kitSum);
            }

            // if (kitItem.customValue) {
            //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
            //   const discountedCustomValue = Decimal(kitItem.customValue)
            //     .times(Decimal(1).minus(discountRate))
            //     .toDecimalPlaces(2);
            //   return Decimal(kitSum).plus(discountedCustomValue);
            // }

            if (!kitItem.value) {
              return Decimal(kitSum);
            }

            const ownDiscount = Decimal(kitItem.discount ? kitItem.discount : 0);
            const kitItemDiscountIsPercentage = kitItem.discountIsPercentage ?? true;

            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { forPrice } = getLinealMetresQuantityInMetres({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthProducts,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(forPrice),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });

              return Decimal(kitSum).plus(productTotalWithDiscount);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                items: kitItem.lengthItems,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(sqmTotal),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });

              return Decimal(kitSum).plus(productTotalWithDiscount);
            }
            if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              const formulaQuantity = getCustomFormulaQuantity({
                quantity: kitItem.quantity,
                formulaOutput: kitItem.customFormulaOutput,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(formulaQuantity),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { forPrice } = getLinealFeetQuantityInFeet({
                items: kitItem.lengthItems,
                minLengthInMm: minLengthLinealFeet,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(forPrice),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
              if (!kitItem.lengthItems?.length) {
                return Decimal(kitSum);
              }
              const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                items: kitItem.lengthItems,
              });
              const productTotalWithDiscount = applyItemDiscount({
                productTotal: Decimal(kitItem.value).times(sqFtTotal),
                discount: ownDiscount,
                discountIsPercentage: kitItemDiscountIsPercentage,
              });
              return Decimal(kitSum).plus(productTotalWithDiscount);
            }

            const quantity = getBasicQuantity({ quantity: kitItem.quantity });
            const discountedItemTotal = applyItemDiscount({
              productTotal: Decimal(kitItem.value).times(quantity),
              discount: ownDiscount,
              discountIsPercentage: kitItemDiscountIsPercentage,
            });
            return Decimal(kitSum).plus(discountedItemTotal);
          }, 0);
          return Decimal(subKitSum).plus(subKitMarkup);
        }, 0);

        return Decimal(sum).plus(kitMarkup).plus(subKitsMarkup);
      }

      if (!item.cost || Decimal(item.cost) <= 0) {
        return Decimal(sum);
      }

      // if (item.customValue) {
      //   const discountRate = sumDiscount.dividedBy(100).toDecimalPlaces(2);
      //   const discountedCustomValue = Decimal(item.customValue)
      //     .times(Decimal(1).minus(discountRate))
      //     .toDecimalPlaces(2);
      //   return Decimal(sum).plus(discountedCustomValue);
      // }

      const ownDiscount = Decimal(item.discount ? item.discount : 0);
      const itemDiscountIsPercentage = item.discountIsPercentage ?? true;

      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { forPrice } = getLinealMetresQuantityInMetres({
          items: item.subitems,
          minLengthInMm: minLengthProducts,
        });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(forPrice),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(productTotalWithDiscount);
      }
      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
          items: item.subitems,
        });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(sqmTotal),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(productTotalWithDiscount);
      }
      if (item.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { forPrice } = getLinealFeetQuantityInFeet({
          items: item.subitems,
          minLengthInMm: minLengthLinealFeet,
        });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(forPrice),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });
        return Decimal(sum).plus(productTotalWithDiscount);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
        if (!item.subitems?.length) {
          return Decimal(sum);
        }
        const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({ items: item.subitems });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(sqFtTotal),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(productTotalWithDiscount);
      }

      if (item.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const formulaQuantity = getCustomFormulaQuantity({
          quantity: item.quantity,
          formulaOutput: item.customFormulaOutput,
        });
        const productTotalWithDiscount = applyItemDiscount({
          productTotal: Decimal(item.value).times(formulaQuantity),
          discount: ownDiscount,
          discountIsPercentage: itemDiscountIsPercentage,
        });

        return Decimal(sum).plus(productTotalWithDiscount);
      }

      const quantity = getBasicQuantity({ quantity: item.quantity });
      const discountedItemTotal = applyItemDiscount({
        productTotal: Decimal(item.value).times(quantity),
        discount: ownDiscount,
        discountIsPercentage: itemDiscountIsPercentage,
      });
      return Decimal(sum).plus(discountedItemTotal);
    }, 0);

  return {
    // labourAdditionalProducts: calculateTotalLabourPrice({ items, sumDiscount }),
    labourAdditionalProducts: calculateTotalLabourPrice({ items }),
    sumAdditionalProducts: sumAdditionalProducts.toFixed(2),
    gstAdditionalProducts: gstAdditionalProducts.toFixed(2),
    costAdditionalProducts: costAdditionalProducts.toFixed(2),
    estimatedCostAdditionalProducts: estimatedCostAdditionalProducts.toFixed(2),
    sumMarkupAdditionalProducts: sumMarkupAdditionalProducts.toFixed(2),
  };
};

export const calcPricesDrawings = (items, taxRate = 0.1) => {
  let sumDrawingsCustomPrices = Decimal(0);
  let GST = Decimal(0);
  const sumDrawingsPrices = Object.keys(items).reduce((price, key) => {
    let drawingPrice = Decimal(0);
    const item = items[key];

    if (!item.isDeleted) {
      const customPrice = item.customPrice ? Decimal(item.customPrice) : Decimal(0);
      sumDrawingsCustomPrices = sumDrawingsCustomPrices.plus(customPrice);

      if (item.customPrice) {
        drawingPrice = customPrice;
      } else if (item.totalPrice) {
        drawingPrice = Decimal(item.totalPrice);
      }
    }

    const linePrice = drawingPrice.toDecimalPlaces(2);
    const lineGst = linePrice.times(Decimal(taxRate)).toDecimalPlaces(2);
    GST = GST.plus(lineGst);
    return Decimal(price).plus(linePrice);
  }, Decimal(0));

  return {
    sumDrawingsCustomPrices: sumDrawingsCustomPrices.toDecimalPlaces(2).toFixed(2),
    sumDrawingsPrices: sumDrawingsPrices.toDecimalPlaces(2).toFixed(2),
    gstDrawingsPrices: GST.toDecimalPlaces(2).toFixed(2),
  };
};

export const calcTotalPriceDrawing = ({ totalLength, pricePerMeter }) => {
  return Decimal(pricePerMeter)
    .times(Decimal(totalLength).dividedBy(1000))
    .toDecimalPlaces(2)
    .toFixed(2);
};

export default findPricePerMetreDrawing;
